import {
  type ContentGenresToLanguages,
  type ContentLanguage,
  type ContentTag,
  type TvShowsGenre,
  type TvShowsLanguage,
} from '@setplex/tria-api'
import { GENRE_ALL, LANGUAGE_ALL } from '~/shared/constants'
import { capitalize } from './strings'

type Language = ContentLanguage | TvShowsLanguage

type FormatGenresToLanguagesParams<T extends Language> = {
  genresToLanguages: ContentGenresToLanguages
  languages: T[]
}

/**
 * Formats the raw genres-to-languages mapping into a Map where:
 * - Keys are genre IDs
 * - Values are arrays of languages available for that genre
 *
 * The function ensures that:
 * 1. LANGUAGE_ALL is always the first language in each array
 * 2. Languages are ordered according to the input languages array
 * 3. GENRE_ALL (id: 0) contains all available languages
 *
 * @param {FormatGenresToLanguagesParams<T>} params - The input parameters
 * @param {ContentGenresToLanguages} params.genresToLanguages - Raw mapping of genres to languages
 * @param {T[]} params.languages - Array of all available languages
 * @returns {Map<number, T[]>} Map of genre IDs to their available languages
 */
export function formatGenresToLanguages<T extends Language>({
  genresToLanguages,
  languages,
}: FormatGenresToLanguagesParams<T>): Map<number, T[]> {
  if (genresToLanguages.length === 0 || languages.length === 0) {
    return new Map<number, T[]>()
  }

  const genreLanguagesMap = new Map<number, T[]>()

  // Add "All Genres" with all available languages
  genreLanguagesMap.set(GENRE_ALL.id, [
    LANGUAGE_ALL as T,
    ...languages.filter((lang) => lang.id !== LANGUAGE_ALL.id),
  ])

  // Create a map of genre IDs to their language IDs
  const genreLanguageIds = new Map<number, Set<number>>()

  genresToLanguages.forEach(({ genreId, languageId }) => {
    if (genreId === GENRE_ALL.id) return
    if (!genreLanguageIds.has(genreId)) {
      genreLanguageIds.set(genreId, new Set())
    }
    genreLanguageIds.get(genreId)?.add(languageId)
  })

  // Set languages for each genre maintaining the order from languages array
  genreLanguageIds.forEach((languageIds, genreId) => {
    genreLanguagesMap.set(genreId, [
      LANGUAGE_ALL as T,
      ...languages.filter(
        (lang) => languageIds.has(lang.id) && lang.id !== LANGUAGE_ALL.id
      ),
    ])
  })

  return genreLanguagesMap
}

type GetContentTitleParams = {
  genre: ContentTag | TvShowsGenre | null
  language: ContentLanguage | TvShowsLanguage | null
  suffix: string
  seoTitleDefault?: string
  seoDescriptionDefault?: string
}

export function getContentInfo({
  genre,
  language,
  suffix,
  seoTitleDefault,
  seoDescriptionDefault,
}: GetContentTitleParams): {
  title: string
  description: string
  seoTitle: string
  seoDescription: string
} {
  const {
    id: genreId,
    title: genreTitle,
    description: genreDescription,
    seoDescription: genreSeoDescription,
  } = genre ?? {}
  const {
    id: languageId,
    title: languageTitle,
    description: languageDescription,
    seoDescription: languageSeoDescription,
  } = language ?? {}

  const isGenreAll = genreId === GENRE_ALL.id
  const isLanguageAll = languageId === LANGUAGE_ALL.id

  if (isGenreAll && isLanguageAll) {
    return {
      title: suffix,
      description: '',
      seoTitle: seoTitleDefault || '',
      seoDescription: seoDescriptionDefault || '',
    }
  }

  let title = ''

  if (!isGenreAll) {
    title = capitalize(genreTitle)
  }
  if (!isLanguageAll) {
    const langTitle = capitalize(languageTitle)
    title = title ? title + ' ' + langTitle : langTitle
  }
  title = title + ' ' + suffix

  return {
    title: title,
    description: genreDescription || languageDescription || '',
    seoTitle: title,
    seoDescription:
      genreSeoDescription ||
      genreDescription ||
      languageSeoDescription ||
      languageDescription ||
      '',
  }
}
